import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { EmphasizedSnackbarContextProvider } from 'Context/EmphasizedSnackbarContext';
import SnackbarDisplay from './SnackbarDisplay';
import { buildErrorMessage } from './utils';

const SnackbarItemsContext = React.createContext(undefined);
const SnackbarFunctionsContext = React.createContext(undefined);

const MAX_ITEMS = 10;

const SNACKBAR_TYPE_MAP = {
  error: 'error',
  success: 'success',
  info: 'info',
  warning: 'warning',
};

// Actions
const PUSH_ITEM = 'push-item';
const CLOSE_ITEM = 'close-item';

// Initialize State
const init = () => {
  return {
    items: [],
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case PUSH_ITEM: {
      const id = (state.items.length < MAX_ITEMS) ? state.items.length : state.items[0].id + 1;
      const {
        timeout = 5000,
      } = action.item.args;

      return {
        ...state,
        items: [{ ...action.item, timeout, id }, ...state.items].slice(0, MAX_ITEMS),
      };
    }
    case CLOSE_ITEM: {
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.id === action.item.id) {
            return {
              ...item,
              open: false,
            };
          }

          return item;
        }),
      };
    }
    default: {
      throw new Error(`Unhandled SnackbarContext action type: ${action.type}`);
    }
  }
};

const SnackbarContextProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const displayErrorMessage = React.useCallback((err, msg, args = {}) => {
    // Don't treat redirects as errors
    if (err?.status === 303) {
      return;
    }

    const message = buildErrorMessage(err, msg);

    dispatch({
      type: PUSH_ITEM,
      item: {
        type: SNACKBAR_TYPE_MAP.error,
        message,
        title: args.title,
        err,
        open: true,
        args,
      },
    });
  }, []);

  const displaySuccess = React.useCallback((message, args = {}) => {
    dispatch({
      type: PUSH_ITEM,
      item: {
        type: SNACKBAR_TYPE_MAP.success,
        message,
        title: args.title,
        args,
        open: true,
      },
    });
  }, []);

  const displayInfo = React.useCallback((message, args = {}) => {
    dispatch({
      type: PUSH_ITEM,
      item: {
        type: SNACKBAR_TYPE_MAP.info,
        message,
        title: args.title,
        args,
        open: true,
      },
    });
  }, []);

  const displayWarning = React.useCallback((message, args = {}) => {
    dispatch({
      type: PUSH_ITEM,
      item: {
        type: SNACKBAR_TYPE_MAP.warning,
        message,
        title: args.title,
        args,
        open: true,
      },
    });
  }, []);

  const handleCloseItem = React.useCallback((item) => {
    dispatch({
      type: CLOSE_ITEM,
      item,
    });
  }, []);

  const memoizedFunctionsValue = React.useMemo(() => {
    return {
      displaySuccess,
      displayInfo,
      displayErrorMessage,
      handleCloseItem,
      displayWarning,
    };
  }, [displaySuccess, displayInfo, displayErrorMessage, displayWarning, handleCloseItem]);

  const memoizedItemsValue = React.useMemo(() => {
    return { items: state.items };
  }, [state]);

  return (
    <SnackbarFunctionsContext.Provider value={memoizedFunctionsValue}>
      <SnackbarItemsContext.Provider value={memoizedItemsValue}>
        <EmphasizedSnackbarContextProvider>
          {children}
        </EmphasizedSnackbarContextProvider>
        <SnackbarDisplay />
      </SnackbarItemsContext.Provider>
    </SnackbarFunctionsContext.Provider>
  );
};

export const useSnackbarFunctionsContext = () => {
  const context = React.useContext(SnackbarFunctionsContext);
  if (context === undefined) {
    throw new Error('useSnackbarContext must be used within a SnackbarContextProvider');
  }
  return context;
};

const useSnackbarContext = () => {
  const itemsContext = React.useContext(SnackbarItemsContext);
  if (itemsContext === undefined) {
    throw new Error('useSnackbarContext must be used within a SnackbarContextProvider');
  }
  const functionsContext = useSnackbarFunctionsContext();
  return useMemo(() => ({ ...functionsContext, ...itemsContext }), [functionsContext, itemsContext]);
};

SnackbarContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  SnackbarContextProvider,
  useSnackbarContext,
};
